import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

/**
 * Synchronizes list-page date UI with restored saved-search state.
 * Keeps the datatable period, date type filter, and active section aligned
 * when a saved search is applied or re-applied.
 */
export const useSavedSearchDateRestore = ({
  activeSection,
  appliedPeriod,
  dateTypeFilter,
  handleDataTablePeriodChange,
  handleSectionChange,
  isSavedSearchesDateEnabled,
  restoredDateTypeFilter,
  savedSearchRestoreKey,
  selectedPeriod,
  setDateTypeFilter
}) => {
  const [lastAppliedSavedSearchRestoreKey, setLastAppliedSavedSearchRestoreKey] = useState(null);

  const shouldRestoreDateType = dateTypeFilter?.id !== restoredDateTypeFilter?.id;
  const shouldRestoreSection =
    !isNil(restoredDateTypeFilter) && activeSection !== restoredDateTypeFilter?.id;

  const syncRestoredPeriod = useCallback(() => {
    if (appliedPeriod) {
      if (!isEqual(appliedPeriod, selectedPeriod)) handleDataTablePeriodChange(appliedPeriod);
      return;
    }

    if (selectedPeriod) handleDataTablePeriodChange(null);
  }, [appliedPeriod, handleDataTablePeriodChange, selectedPeriod]);

  useEffect(() => {
    const shouldRestore =
      isSavedSearchesDateEnabled &&
      !!savedSearchRestoreKey &&
      lastAppliedSavedSearchRestoreKey !== savedSearchRestoreKey;

    if (!shouldRestore) return;

    syncRestoredPeriod();

    if (shouldRestoreDateType) setDateTypeFilter(restoredDateTypeFilter);
    if (shouldRestoreSection) handleSectionChange?.(null, restoredDateTypeFilter?.id);

    setLastAppliedSavedSearchRestoreKey(savedSearchRestoreKey);
  }, [
    activeSection,
    appliedPeriod,
    dateTypeFilter?.id,
    handleDataTablePeriodChange,
    handleSectionChange,
    isSavedSearchesDateEnabled,
    lastAppliedSavedSearchRestoreKey,
    restoredDateTypeFilter,
    savedSearchRestoreKey,
    selectedPeriod,
    shouldRestoreDateType,
    shouldRestoreSection,
    syncRestoredPeriod,
    setDateTypeFilter
  ]);

  const resetRestoreKey = useCallback(() => setLastAppliedSavedSearchRestoreKey(null), []);

  return { resetRestoreKey };
};
